import { ApiError, type ErrorCode } from './errors';
import { newIdempotencyKey } from './idempotency';
import { notifyUnauthenticated, session } from './session';
import i18n from '../i18n';

/** The API answers its messages (errors) in the language the user selected. */
const language = () => (i18n.language || 'fr').slice(0, 2);

// Development: empty = same origin through the Vite proxy. Production: the API origin (the backend must allow it via CORS_ORIGINS).
const ROOT = ((import.meta.env.VITE_API_URL as string | undefined) ?? '').replace(/\/$/, '');

type Query = Record<string, string | number | boolean | undefined | null>;
export interface RequestOptions {
  query?: Query;
  body?: unknown;
  /** Send an Idempotency-Key. Pass a key to reuse it across retries; `true` creates a fresh one. */
  idempotencyKey?: string | true;
  signal?: AbortSignal;
  /** Do not treat 401 as "session ended" (used by the login call). */
  public?: boolean;
  /** Send this token instead of the user's session (the platform console has its own); a 401 is then left to the caller. */
  bearer?: string;
}

function buildUrl(path: string, query?: Query) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return `${ROOT}/api/v1${path}${s ? `?${s}` : ''}`;
}

async function toError(res: Response): Promise<ApiError> {
  let body: any = null;
  try { body = await res.json(); } catch { /* not JSON */ }
  const e = body?.error;
  if (e?.code) return new ApiError(e.code as ErrorCode, String(e.message ?? res.statusText), res.status, e.details, e.requestId);
  // Framework-level answers (e.g. a 400 from the validation pipe) are not enveloped.
  if (res.status === 400 && body) return new ApiError('VALIDATION_ERROR', Array.isArray(body.message) ? String(body.message[0]) : String(body.message ?? 'Invalid request'), 400, body);
  const code: ErrorCode = res.status === 401 ? 'UNAUTHENTICATED' : res.status === 403 ? 'FORBIDDEN' : res.status === 404 ? 'NOT_FOUND' : res.status >= 500 ? 'INTERNAL_ERROR' : 'CONFLICT';
  return new ApiError(code, res.statusText || 'Request failed', res.status);
}

interface AccessToken { accessToken: string; expiresIn: number; companyId?: string }
let refreshing: Promise<boolean> | null = null;

/**
 * Exchanges the refresh cookie for a new access token (the server rotates the cookie at the same time). Single flight in this tab, and one tab at a
 * time across the browser (Web Locks): refresh tokens work once, so two parallel exchanges of the same cookie would be read by the server as token
 * theft and end the session.
 */
export function refreshSession(): Promise<boolean> {
  const exchange = async () => {
    try {
      const res = await fetch(buildUrl('/auth/refresh'), { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Refresh-Cookie': '1' }, body: '{}' });
      if (!res.ok) return false;
      const r = (await res.json()) as AccessToken;
      session.set(r.accessToken, r.expiresIn);
      return true;
    } catch { return false; }
  };
  refreshing ??= (async () => {
    try { return typeof navigator !== 'undefined' && navigator.locks ? await navigator.locks.request('sidinet-auth-refresh', exchange) : await exchange(); }
    finally { setTimeout(() => { refreshing = null; }, 0); }
  })();
  return refreshing;
}

/** At start-up (page reload, new tab): open the session again from the refresh cookie, if there is one. */
export const restoreSession = () => (session.token ? Promise.resolve(true) : refreshSession());

async function raw(method: string, path: string, o: RequestOptions, retried = false): Promise<Response> {
  // X-Refresh-Cookie: the auth routes put the refresh token in an httpOnly cookie instead of the response body
  const headers: Record<string, string> = { Accept: 'application/json', 'Accept-Language': language(), 'X-Request-Id': newIdempotencyKey(), 'X-Refresh-Cookie': '1' };
  const bearer = o.bearer ?? session.token;
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  if (o.body !== undefined) headers['Content-Type'] = 'application/json';
  if (o.idempotencyKey) headers['Idempotency-Key'] = o.idempotencyKey === true ? newIdempotencyKey() : o.idempotencyKey;
  let res: Response;
  try {
    // credentials: the API may live on another sub-domain; the only cookie it sets is the refresh cookie of the auth routes
    res = await fetch(buildUrl(path, o.query), { method, headers, body: o.body !== undefined ? JSON.stringify(o.body) : undefined, signal: o.signal, credentials: 'include' });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError('NETWORK_ERROR', 'The server cannot be reached', 0);
  }
  if (!res.ok) {
    const err = await toError(res);
    if (res.status === 401 && !o.public && !o.bearer) {
      // An expired access token is routine: renew it once and replay the request (the Idempotency-Key header travels with it, so a replay can never double-apply).
      if (!retried && (await refreshSession())) return raw(method, path, o, true);
      session.clear(); notifyUnauthenticated();
    }
    throw err;
  }
  return res;
}

async function json<T>(method: string, path: string, o: RequestOptions = {}): Promise<T> {
  const res = await raw(method, path, o);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const http = {
  get: <T>(path: string, o?: Omit<RequestOptions, 'body'>) => json<T>('GET', path, o),
  post: <T>(path: string, body?: unknown, o?: Omit<RequestOptions, 'body'>) => json<T>('POST', path, { ...o, body: body ?? {} }),
  put: <T>(path: string, body?: unknown, o?: Omit<RequestOptions, 'body'>) => json<T>('PUT', path, { ...o, body: body ?? {} }),
  patch: <T>(path: string, body?: unknown, o?: Omit<RequestOptions, 'body'>) => json<T>('PATCH', path, { ...o, body: body ?? {} }),
  delete: <T>(path: string, body?: unknown, o?: Omit<RequestOptions, 'body'>) => json<T>('DELETE', path, { ...o, body }),
  /** Upload a file as the raw request body (attachments): the metadata travels in the query string. */
  async upload<T>(path: string, query: Query, file: Blob, o: Omit<RequestOptions, 'body' | 'query'> = {}): Promise<T> {
    const send = async (retried: boolean): Promise<Response> => {
      const headers: Record<string, string> = { Accept: 'application/json', 'Accept-Language': language(), 'Content-Type': 'application/octet-stream', 'X-Request-Id': newIdempotencyKey() };
      if (session.token) headers.Authorization = `Bearer ${session.token}`;
      let res: Response;
      try { res = await fetch(buildUrl(path, query), { method: 'POST', headers, body: file, signal: o.signal }); }
      catch (e) { if ((e as Error).name === 'AbortError') throw e; throw new ApiError('NETWORK_ERROR', 'The server cannot be reached', 0); }
      if (!res.ok) {
        const err = await toError(res);
        if (res.status === 401 && !retried && (await refreshSession())) return send(true);
        if (res.status === 401) { session.clear(); notifyUnauthenticated(); }
        throw err;
      }
      return res;
    };
    const res = await send(false);
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  },
  /** Authenticated file download: the bearer token cannot travel on a bare <a href>. */
  async download(path: string, o: Omit<RequestOptions, 'body'> = {}) {
    const res = await raw('GET', path, o);
    const dispo = res.headers.get('Content-Disposition') ?? '';
    const filename = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(dispo)?.[1];
    return { blob: await res.blob(), filename: filename ? decodeURIComponent(filename) : 'download', sha256: res.headers.get('X-Content-SHA256') };
  },
};

export interface Page<T> { items: T[]; nextCursor: string | null }
